import { useEffect, useRef, useState } from "react";
import { registerToastSink, type ToastPayload } from "../../../integrations/tools/outlook/toast-bridge";
import { CheckIcon, WarningIcon } from "../../shared/components/icons";
import styles from "./ChatToaster.module.css";

// Within the 2–3s the spec asks for (AIVA-9183).
export const TOAST_TIMEOUT_MS = 2500;

/**
 * Custom toast for the chat panel.
 *
 * Replaces Fluent UI's ``<Toaster>`` because Fluent's auto-dismiss timer is
 * implemented as a CSS animation whose ``animation-duration`` equals the
 * configured timeout (see ``@fluentui/react-toast/lib/components/Timer``).
 * Office add-in WebViews — particularly Outlook Desktop's WebView2 on
 * Windows with system-level "reduce motion" enabled — collapse all CSS
 * animation durations to ~0ms, which makes Fluent's ``onAnimationEnd``
 * dismiss the toast within a single frame. The user sees the toast
 * appear and vanish instantly regardless of the timeout value passed.
 *
 * setTimeout doesn't suffer the same fate, so we drive both show and
 * dismiss from React state. Pause-on-hover is wired by clearing the
 * timeout on mouseenter and restarting it on mouseleave.
 *
 * Mounted above everything docked over the composer (quick-action chips,
 * sheets) so it covers neither those nor the input (AIVA-9183).
 */
export function ChatToaster(): React.ReactElement | null {
  const [toast, setToast] = useState<ToastPayload | null>(null);
  const dismissTimerRef = useRef<number | null>(null);
  const hoveredRef = useRef(false);

  const scheduleDismiss = (): void => {
    if (dismissTimerRef.current !== null) {
      globalThis.clearTimeout(dismissTimerRef.current);
    }
      dismissTimerRef.current = globalThis.setTimeout(() => {
      setToast(null);
      dismissTimerRef.current = null;
    }, TOAST_TIMEOUT_MS);
  };

  useEffect(() => {
    const unsubscribe = registerToastSink((payload) => {
      setToast(payload);
      if (!hoveredRef.current) {
        scheduleDismiss();
      }
    });
    return () => {
      unsubscribe();
      if (dismissTimerRef.current !== null) {
        globalThis.clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, []);

  if (!toast) return null;

  const handleMouseEnter = (): void => {
    hoveredRef.current = true;
    if (dismissTimerRef.current !== null) {
      globalThis.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  };

  const handleMouseLeave = (): void => {
    hoveredRef.current = false;
    scheduleDismiss();
  };

  const intentClass = styles[toast.intent] ?? styles.info;

  // <output> has the implicit ARIA ``status`` role and ``aria-live="polite"``
  // baked in, so the previous ``<div role="status" aria-live="polite">``
  // pattern is redundant and triggers Sonar's accessibility rule. Use the
  // native element for the same announce behaviour without the explicit
  // role.
  return (
    <div className={styles.anchor}>
      <div className={styles.wrapper}>
        <output
          className={`${styles.toast} ${intentClass}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <span className={styles.icon}>{toast.intent === "success" ? <CheckIcon /> : <WarningIcon />}</span>
          {toast.title}
        </output>
      </div>
    </div>
  );
}
